import { Check, Minus, ArrowRight } from "@phosphor-icons/react";

const INTAKE_URL = "/intake";

const features = [
  { label: "Price", essentials: "$89/mo", coaching: "$179/mo", vip: "$399/mo", bespoke: "Custom", emphasize: true },
  { label: "Daily breakdown", essentials: "<$3/day", coaching: "<$6/day", vip: "<$14/day", bespoke: "Quoted" },
  { label: "Best for", essentials: "Self-starters", coaching: "Most clients", vip: "Max support", bespoke: "Bespoke scope" },
  { label: "Everfit app access", essentials: true, coaching: true, vip: true, bespoke: true },
  { label: "Fitness assessment + onboarding", essentials: true, coaching: true, vip: true, bespoke: "Custom" },
  { label: "Custom workout program", essentials: true, coaching: true, vip: true, bespoke: "Custom" },
  { label: "Exercise demo videos", essentials: true, coaching: true, vip: true, bespoke: "Custom" },
  { label: "Progress + habit tracking", essentials: true, coaching: true, vip: true, bespoke: "Custom" },
  { label: "First week feedback check-in", essentials: true, coaching: true, vip: true, bespoke: "Custom" },
  { label: "Monthly accountability check-in", essentials: true, coaching: true, vip: true, bespoke: "Custom" },
  { label: "Weekly accountability check-ins", essentials: false, coaching: true, vip: true, bespoke: "Custom" },
  { label: "Messaging support", essentials: false, coaching: "Yes", vip: "Priority", bespoke: "Custom" },
  { label: "Form review", essentials: false, coaching: "Yes", vip: "Priority", bespoke: "Custom" },
  { label: "Nutrition guidance", essentials: false, coaching: "Basic", vip: "Advanced", bespoke: "Custom" },
  { label: "Habit coaching", essentials: "Light", coaching: "Yes", vip: "Advanced", bespoke: "Custom" },
  { label: "Program updates", essentials: "Every 4 wks", coaching: "As needed", vip: "Real-time", bespoke: "Custom" },
  { label: "Coaching calls", essentials: false, coaching: "Initial strategy", vip: "Monthly 1:1", bespoke: "Custom" },
  { label: "Voice / video feedback", essentials: false, coaching: "As needed", vip: "Weekly", bespoke: "Custom" },
  { label: "Travel + schedule adjustments", essentials: "Monthly only", coaching: "As needed", vip: "Priority", bespoke: "Custom" },
  { label: "Lifestyle coaching", essentials: false, coaching: "Basic", vip: true, bespoke: "Custom" },
  { label: "Event-specific coaching", essentials: false, coaching: "Limited", vip: true, bespoke: "Custom" },
  { label: "Quarterly comprehensive review", essentials: false, coaching: false, vip: true, bespoke: "Custom" },
  { label: "In-person sessions (if local)", essentials: false, coaching: false, vip: "Optional", bespoke: "Custom" },
  { label: "Sport-specific work", essentials: false, coaching: false, vip: "Optional", bespoke: "Custom" },
  { label: "Couples / partner training", essentials: false, coaching: false, vip: false, bespoke: "Custom" },
];

function Cell({ val, accent }) {
  if (val === true) {
    return (
      <div className={`flex justify-center items-center ${accent ? "text-[#FF1B91]" : "text-white"}`}>
        <Check size={18} weight="bold" />
      </div>
    );
  }
  if (val === false) {
    return (
      <div className="flex justify-center items-center text-white/25">
        <Minus size={18} weight="bold" />
      </div>
    );
  }
  return (
    <div className={`text-center text-sm ${accent ? "text-[#FF1B91] font-bold" : "text-white/85"}`}>
      {val}
    </div>
  );
}

export default function Compare() {
  return (
    <section
      id="compare"
      data-testid="compare-section"
      className="relative bg-black py-16 lg:py-20 border-b border-white/10 overflow-hidden px-6 md:px-12 lg:px-20"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-14">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#FF1B91]" />
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Compare Plans / 06</p>
          </div>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.88] tracking-tighter mb-6">
            Four plans.{" "}
            <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">built around real life.</span>
          </h2>
          <p className="text-white/70 leading-relaxed max-w-md mb-8">
            Choose the level of support that fits your schedule, your goals, and how much accountability you want. All programs delivered through the Everfit app — workouts, check-ins, progress tracking, habit tracking, and coaching support in one place.
          </p>
          <a
            href={INTAKE_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="compare-intake-cta"
            className="inline-flex items-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink"
          >
            Not sure? Start the intake <ArrowRight size={16} weight="bold" />
          </a>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:gap-3">
            {[
              { name: "Essentials", price: "$89", accent: false },
              { name: "Coaching", price: "$179", accent: true },
              { name: "VIP", price: "$399", accent: false },
              { name: "Bespoke", price: "Custom", accent: false },
            ].map((p) => (
              <div
                key={p.name}
                className={`text-center p-4 lg:p-5 border ${
                  p.accent ? "border-[#FF1B91] bg-[#FF1B91]/5" : "border-white/10 bg-[#0F0F0F]"
                }`}
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-semibold mb-2">
                  {p.name}
                </div>
                <div className="font-display font-black text-2xl lg:text-3xl text-white">{p.price}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1">{p.price === "Custom" ? "Quoted" : "/ month"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-white/10 overflow-hidden overflow-x-auto">
        <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr] bg-[#0F0F0F] border-b border-white/10 min-w-[720px]">
          <div className="px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-white/50 font-semibold">Feature</div>
          <div className="px-3 py-4 text-[10px] uppercase tracking-[0.25em] text-white/80 font-bold text-center border-l border-white/10">Essentials</div>
          <div className="px-3 py-4 text-[10px] uppercase tracking-[0.25em] text-[#FF1B91] font-bold text-center border-l border-white/10 bg-[#FF1B91]/5">Coaching</div>
          <div className="px-3 py-4 text-[10px] uppercase tracking-[0.25em] text-white/80 font-bold text-center border-l border-white/10">VIP</div>
          <div className="px-3 py-4 text-[10px] uppercase tracking-[0.25em] text-white/80 font-bold text-center border-l border-white/10">Bespoke</div>
        </div>

        {features.map((f, i) => (
          <div
            key={f.label}
            className={`grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr] border-b border-white/5 hover:bg-white/[0.02] transition-colors min-w-[720px] ${
              f.emphasize ? "bg-white/[0.03]" : ""
            }`}
            data-testid={`compare-row-${i}`}
          >
            <div className={`px-5 py-3.5 text-sm ${f.emphasize ? "text-white font-bold uppercase tracking-wider text-xs" : "text-white/80"}`}>
              {f.label}
            </div>
            <div className="px-3 py-3.5 border-l border-white/5"><Cell val={f.essentials} /></div>
            <div className="px-3 py-3.5 border-l border-white/5 bg-[#FF1B91]/5"><Cell val={f.coaching} accent={f.emphasize} /></div>
            <div className="px-3 py-3.5 border-l border-white/5"><Cell val={f.vip} /></div>
            <div className="px-3 py-3.5 border-l border-white/5"><Cell val={f.bespoke} /></div>
          </div>
        ))}
      </div>

      <p className="mt-8 text-center text-sm text-white/60 max-w-2xl mx-auto">
        Still not sure which option fits? <a href={INTAKE_URL} target="_blank" rel="noreferrer noopener" data-testid="compare-intake-link" className="text-[#FF1B91] font-semibold hover:underline">Start your intake</a> and I&apos;ll help you choose the best level of support.
      </p>
    </section>
  );
}
